import { Component, OnInit } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AccountInfo, InteractionStatus } from '@azure/msal-browser';
import { filter } from 'rxjs/operators';
import { NgxSpinnerService } from 'ngx-spinner';
import { CommitTrackerService } from './committracker.service';
import { IBuildMatch, ICommitSearchResult, IReleaseInfo } from '../shared/commitSearch';

interface ICommitRow {
  sha: string;
  buildNumber: string;
  buildId: number;
  environments: { [name: string]: string };
  hasRelease: boolean;
}

@Component({
  selector: 'app-committracker',
  templateUrl: './committracker.component.html',
  styleUrls: ['./committracker.component.css']
})
export class CommittrackerComponent implements OnInit {

  public workItemId: number = null;
  public result: ICommitSearchResult = null;
  public error: string = '';
  public searched: boolean = false;
  public loginDisplay = false;
  public message: string = 'You must login to view this page.';
  public selectedEnvironment: string = null;
  public showReleaseDetails: boolean = false;

  private readonly envOrder = ['Dev', 'QA', 'Staging', 'PreProd', 'Production', 'Prod Appsettings'];

  constructor(
    private service: CommitTrackerService,
    private spinner: NgxSpinnerService,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService
  ) { }

  ngOnInit() {
    this.handleSecurity();
  }

  handleSecurity() {
    this.authService.handleRedirectObservable().subscribe({
      next: (result) => {
        if (result) {
          this.authService.instance.setActiveAccount(result.account);
        }
        this.setLoginDisplay();
      },
      error: (error) => console.log(error)
    });

    this.msalBroadcastService.inProgress$
      .pipe(filter((status: InteractionStatus) => status === InteractionStatus.None))
      .subscribe(() => {
        this.setLoginDisplay();
      });
  }

  setLoginDisplay() {
    let accounts: AccountInfo[] = this.authService.instance.getAllAccounts();
    this.loginDisplay = accounts.length > 0;
  }

  search() {
    if (!this.workItemId) return;
    this.spinner.show();
    this.error = '';
    this.result = null;
    this.searched = false;
    this.selectedEnvironment = null;
    this.showReleaseDetails = false;

    this.service.search(this.workItemId).subscribe({
      next: (data) => {
        this.result = data;
        this.searched = true;
        this.spinner.hide();
      },
      error: (_err) => {
        this.error = 'Error searching for commit. Please check the project and commit ID.';
        this.searched = true;
        this.spinner.hide();
      }
    });
  }

  selectEnvironment(name: string) {
    this.selectedEnvironment = this.selectedEnvironment === name ? null : name;
  }

  // ── Computed: all unique environment names in deployment order ──────────

  get allEnvironmentNames(): string[] {
    if (!this.result) return [];
    const names = new Set<string>();
    for (const build of this.result.builds) {
      for (const release of build.releases) {
        for (const env of release.environments) {
          names.add(env.name);
        }
      }
    }
    const ordered = this.envOrder.filter(e => names.has(e));
    const rest = Array.from(names).filter(n => !this.envOrder.includes(n));
    return [...ordered, ...rest];
  }

  // ── Computed: per-commit row with best status per environment ───────────

  get commitMatrix(): ICommitRow[] {
    if (!this.result) return [];
    const allCommits = this.result.allLinkedCommits || [];
    return allCommits.map(sha => {
      // A commit can appear in multiple builds (e.g. rebuilt, or picked up by different pipelines).
      // Aggregate environment statuses across all matching builds so no release is missed.
      const matchingBuilds = this.result.builds.filter(
        b => b.sourceVersion?.toLowerCase() === sha.toLowerCase()
      );
      const environments: { [name: string]: string } = {};
      for (const build of matchingBuilds) {
        for (const release of build.releases) {
          for (const env of release.environments) {
            const cur = environments[env.name];
            if (!cur || this.statusPriority(env.status) > this.statusPriority(cur)) {
              environments[env.name] = env.status;
            }
          }
        }
      }
      const buildNumbers = matchingBuilds.map(b => b.buildNumber).filter(Boolean);
      return {
        sha,
        buildNumber: buildNumbers.join(', ') || null,
        buildId: matchingBuilds[0]?.buildId ?? null,
        environments,
        hasRelease: matchingBuilds.some(b => b.releases.length > 0)
      };
    });
  }

  // ── Computed: per-environment summary (succeeded count / total) ─────────

  get environmentSummary(): { name: string; succeededCount: number; total: number }[] {
    const matrix = this.commitMatrix;
    const total = matrix.length;
    return this.allEnvironmentNames.map(name => ({
      name,
      succeededCount: matrix.filter(r => (r.environments[name] || '').toLowerCase() === 'succeeded').length,
      total
    }));
  }

  get selectedEnvSummary(): { name: string; succeededCount: number; total: number } {
    if (!this.selectedEnvironment) return null;
    return this.environmentSummary.find(e => e.name === this.selectedEnvironment) || null;
  }

  // ── Computed: release groups for the collapsible details section ────────

  get releaseGroups(): { release: IReleaseInfo; builds: IBuildMatch[] }[] {
    if (!this.result) return [];
    const map = new Map<number, { release: IReleaseInfo; builds: IBuildMatch[] }>();
    for (const build of this.result.builds) {
      for (const release of build.releases) {
        if (!map.has(release.releaseId)) {
          map.set(release.releaseId, { release, builds: [] });
        }
        map.get(release.releaseId).builds.push(build);
      }
    }
    return Array.from(map.values());
  }

  get unreleasedBuilds(): IBuildMatch[] {
    if (!this.result) return [];
    return this.result.builds.filter(b => b.releases.length === 0);
  }

  // ── Status helpers ──────────────────────────────────────────────────────

  private statusPriority(status: string): number {
    if (!status) return 0;
    const s = status.toLowerCase();
    if (s === 'succeeded') return 4;
    if (s === 'inprogress') return 3;
    if (s === 'rejected' || s === 'canceled') return 2;
    return 1;
  }

  statusClass(status: string): string {
    if (!status) return 'env-unknown';
    const s = status.toLowerCase();
    if (s === 'succeeded') return 'env-succeeded';
    if (s === 'inprogress') return 'env-inprogress';
    if (s === 'rejected' || s === 'canceled') return 'env-failed';
    return 'env-pending';
  }

  statusIcon(status: string): string {
    if (!status) return '—';
    const s = status.toLowerCase();
    if (s === 'succeeded') return '✓';
    if (s === 'inprogress') return '↻';
    if (s === 'rejected' || s === 'canceled') return '✗';
    return '—';
  }

  shortSha(sha: string): string {
    return sha ? sha.substring(0, 8) : '—';
  }
}
